import { useState } from "react";
import { Coins, DollarSign } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AGENT_INCOME_HISTORY } from "@/lib/mockData";
import { AIF_INCOME_HISTORY } from "./aifIncomeMock";
import { C } from "./homeTheme";
import { useTranslation } from "react-i18next";
import { getTokenName } from "@/config/common";
import { CCC_AIF_ADDRESS, USDT_ADDRESS } from "@/config/env";
import { formatUsdPrefix } from "@/lib/utils";

export function IncomeHistoryDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"usdt" | "aif">("usdt");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-sm mx-auto"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("homePage.incomeHistoryDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <div
          className="flex rounded-lg overflow-hidden mb-3"
          style={{ background: C.card2, border: `1px solid ${C.border}` }}
        >
          <button
            className="flex-1 py-2 text-xs font-semibold transition-all flex items-center justify-center gap-1.5"
            style={{
              background: tab === "usdt" ? `${C.primary}12` : "transparent",
              color: tab === "usdt" ? C.primary : C.dim,
            }}
            onClick={() => setTab("usdt")}
          >
            <DollarSign size={12} />
            {t("homePage.incomeHistoryDialog.usdtTab", {
              tokenNameUsdt: getTokenName(USDT_ADDRESS),
            })}
          </button>
          <button
            className="flex-1 py-2 text-xs font-semibold transition-all flex items-center justify-center gap-1.5"
            style={{
              background: tab === "aif" ? `${C.amber}12` : "transparent",
              color: tab === "aif" ? C.amber : C.dim,
            }}
            onClick={() => setTab("aif")}
          >
            <Coins size={12} />
            {t("homePage.incomeHistoryDialog.aifTab", {
              tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
            })}
          </button>
        </div>

        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {tab === "usdt"
            ? AGENT_INCOME_HISTORY.map((item, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center p-3 rounded-lg"
                  style={{
                    background: C.card2,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <div>
                    <div className="text-xs font-medium">
                      {t("homePage.incomeHistoryDialog.staticIncome", {
                        rate: item.rate,
                      })}
                    </div>
                    <div className="text-[10px]" style={{ color: C.dim }}>
                      {t("homePage.incomeHistoryDialog.dateZone", {
                        date: item.date,
                        volume: item.zoneVolume,
                      })}
                    </div>
                  </div>
                  <div
                    className="mono-num font-bold text-sm"
                    style={{ color: C.green }}
                  >
                    +{formatUsdPrefix(String(item.amount))}
                  </div>
                </div>
              ))
            : AIF_INCOME_HISTORY.map((item, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center p-3 rounded-lg"
                  style={{
                    background: C.card2,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <div>
                    <div className="text-xs font-medium">
                      {t("homePage.incomeHistoryDialog.aifDistribution", {
                        tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
                      })}
                    </div>
                    <div className="text-[10px]" style={{ color: C.dim }}>
                      {t("homePage.incomeHistoryDialog.aifDateDetail", {
                        date: item.date,
                        usdValue: formatUsdPrefix(String(item.usdValue)),
                        price: formatUsdPrefix(String(item.aifPrice)),
                      })}
                    </div>
                  </div>
                  <div
                    className="mono-num font-bold text-sm"
                    style={{ color: C.amber }}
                  >
                    +{item.aifAmount.toFixed(2)} {getTokenName(CCC_AIF_ADDRESS)}
                  </div>
                </div>
              ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
